import 'lazysizes'
import { NextPage } from 'next'
import Head from 'next/head'
import FAQ, { FAQItem } from '#/components/FAQ'
import Link from '#/components/Link'
import Page from '#/components/Page'
import cn from '#/utils/classnames'
import styles from './styles.module.scss'

const News: NextPage = () => {
  const items: FAQItem[] = [
    {
      question: 'Who is the TopHap team?',
      answer: (
        <div>
          TopHap is a team of real estate industry veterans and data visualization
          experts. We have built brokerages and founded technology companies that
          have transformed other industries. We are battle-worn home buyers and
          sellers who feel the acute need for better real estate information and
          tools.
        </div>
      )
    },
    {
      question:
        'How does TopHap understand the needs of real estate professionals?',
      answer: (
        <div>
          Because we’ve been in your shoes. As real estate industry veterans of
          nearly 20 years, we built TopHap to make real estate research more
          efficient and reliable.
        </div>
      )
    },
    {
      question: 'How is TopHap unique from other real estate search portals?',
      answer: (
        <div>
          TopHap is a real estate technology company that makes investor-grade
          analytics easy, intuitive, and accessible to everyone. While other
          platforms focus on property search and promotion we focus on
          {' '}
          <b>research</b>
          {' '}
          and providing data-driven insights, TopHap provides
          additional tools such as analytics, 3D visualizations, and artificial
          intelligence to make real estate research more efficient.
          <br />
          <br />
          What makes TopHap different from the existing online real estate
          platforms is that they all focus on search, while we focus on research.
          Their value is promoting properties while our value is providing
          data-driven market insights. Real estate analysis tools of this depth
          simply do not exist and there isn’t another product like TopHap!
        </div>
      )
    },
    {
      question: 'How can TopHap help me?',
      answer: (
        <div>
          <ul>
            <li>
              <b>Real Estate Agents:</b>
              {' '}
              We help you by making you an expert in
              any territory and putting the power of data and analytics at your
              fingertips so you can be a trusted partner for your clients.
            </li>
            <li>
              <b>Investors:</b>
              {' '}
              We help you by giving you the enterprise-level
              tools you can deeply evaluate opportunities and make confident
              investment decisions about where your money will generate the
              highest returns in the ever-changing markets.
            </li>
            <li>
              <b>Builders:</b>
              {' '}
              We help you by showing you where the hot markets
              are and what the trends and preferences are in those markets so you
              can build a product that will bring top dollar and people will love.
            </li>
            <li>
              <b>Buyers:</b>
              {' '}
              We help you by allowing you to quickly understand
              what homes are really worth in a market, so you can gain confidence
              in what to buy, where to buy and when to buy it.
            </li>
            <li>
              <b>Sellers:</b>
              {' '}
              We help you by showing you what is really driving
              trends in a market so you can maximize the timing, positioning, and
              pricing of your home.
            </li>
          </ul>
        </div>
      )
    },
    {
      question: 'Where does TopHap get the data to power it’s AI?',
      answer: (
        <div>
          We combine public residential real estate data with macro-economic,
          environmental, geographic, census, construction, local commercial data,
          and many other public records to derive insights and correlations that
          power our valuation engine and market assessment tools. All together,
          this dataset is the most comprehensive collection of public and
          proprietary data ever to be integrated into a single real estate
          platform. All this data is then applied to state of the art artificial
          intelligence and visualization techniques to deliver the most robust and
          accurate real estate assessment tools in the industry.
        </div>
      )
    },
    {
      question: 'What is the best way to learn about TopHap’s powerful features?',
      answer: (
        <div>
          We recommend that you watch our short and helpful training videos on {' '}
          <Link className='underline' href='/academy'>TopHap Academy</Link>, see what new features we have announced on our <Link className='underline' href='/whats-new'>what's new page</Link>, and check out the <Link className='underline' href='/gallery'>Gallery</Link> to see interactive examples of useful maps and searches found on TopHap.
          {' '}
          and then try it out for
          yourself with our free 14 day trial!
        </div>
      )
    },
    {
      question:
        'What features do I get in the paid TopHap subscriptions versus the free version?',
      answer: (
        <div>
          TopHap features a free version and two paid subscription plans called
          Pro and Expert. Paid subscriptions include additional features such as
          Market Analytics, Property Analytics, CMA+, Value Forecasting, and CSV
          exports to help you take to your market intelligence to the next level.
          You can learn more about TopHap subscriptions and pricing {' '}
          <Link className='underline' href='/pricing'>here</Link>
          .
        </div>
      )
    },
    {
      question: 'How does the pricing work for TopHap paid subscriptions?',
      answer: (
        <div>
          TopHap provides four tiers of subscription plans: Free, Basic, Pro, and
          Expert. Paid subscriptions include additional features such as Market
          Analytics, Property Analytics, CMA+, Value Forecasting, and CSV exports.
          {' '}
          <br />
          <br />
          The free version of TopHap is available to all users without a credit
          card or registration. TopHap Basic is our entry-level plan that
          costs $11 per user per month when billed annually or $15 per user per month
          when billed monthly. TopHap Professional costs $45 per user per month
          when billed annually and $55 per user per month when billed monthly. The
          price of TopHap Expert is $126 per user per month when billed annually
          and $155 when billed monthly. You can learn more about TopHap
          subscriptions and pricing
          {' '}
          <Link className='underline' href='/pricing'>here</Link>
          .
        </div>
      )
    },
    {
      question: 'Is there a version of TopHap for real estate teams?',
      answer: (
        <div>
          TopHap subscriptions are for individual professionals, but if you’d like
          to explore subscription options for your entire team, we’d love to chat
          with you. Email us at
          <a href='mailto:hello@tophap.com'> hello@tophap.com</a>
          .
        </div>
      )
    },
    {
      question: 'When will TopHap be available outside of California?',
      answer: (
        <div>
          It already is! TopHap launched nationally in April 2022. Read more about <Link href='/whats-new/tophap-is-nation-wide/'>TopHap's national launch</Link>.
        </div>
      )
    },
    {
      question: 'Do you have any plans to offer API access to your data?',
      answer: (
        <div>
          Our API isn’t public yet, but If you are interested in using our API,
          email us at
          {' '}
          <a href='mailto:hello@tophap.com'> hello@tophap.com</a>
          {' '}
          and
          let us know your use-case.
        </div>
      )
    },
    {
      question:
        'My client is asking about value estimates. Is there a way to share an unlocked property view?',
      answer: (
        <div>
          If you have a TopHap subscription then you can share a property view
          with a client and they will see a fully unlocked version.
        </div>
      )
    },
    {
      question: 'What is the difference between the ARV and the TopHap estimate?',
      answer: (
        <div>
          The ARV Calculator is the most recent and accurate estimate because it
          updates real time as you interact with it. It takes the latest market
          data and combines it with your input to give you a super estimate.
          Meanwhile, the TopHap Estimate is updated once per month and influenced
          primarily on market and environmental factors, rather than your input.
        </div>
      )
    },
    {
      question:
        'The platform is cool but how do we know if the AI analytics are accurate?',
      answer: (
        <div>
          All of the estimates on TopHap are tested against historic data to
          ensure the accuracy of our models. You can see how well the model
          performs by enabling the estimate accuracy layer. All the other
          analytics are coming from trusted sources. For example the noise layer
          comes from the department of transportation. The community layers come
          from the census. The 3D topography comes from satellite imagery from the
          last space shuttle missions.
        </div>
      )
    },
    {
      question:
        'How do I know that the TopHap AVM is more accurate than the “other” estimate?',
      answer: (
        <div>
          The “other” estimates are primarily based on advertising prices, which
          don’t necessarily reflect the true value of a property. You have
          probably noticed that when a property is listed it jumps or drops to
          match the list price. We don&apos;t use the list price and the estimates
          are for true market value. The TopHap AVM is generated using 80+ data
          layers encapsulating important information like pollution, weather,
          disaster likelihood, sound level, and more, creating a much more
          realistic and transparent valuation of the property. The “other”
          estimates are like trying to read a book without your reading glasses.
        </div>
      )
    },
    {
      question: 'How often is the data on TopHap updated?',
      answer: (
        <div>
          Our data is updated on the following schedule:
          <div className={styles['th-update-schedule']}>
            <div className={styles['th-faq-group']}>
              <div className={styles['th-faq-column']}>
                <div className={cn(styles['th-faq-column-header'], styles['th-weekly'])}>Weekly</div>
                <div>
                  <ul>
                    <li>Recorder Data</li>
                  </ul>
                </div>
              </div>
              <div className={styles['th-faq-column']}>
                <div className={cn(styles['th-faq-column-header'], styles['th-monthly'])}>Monthly</div>
                <div>
                  <ul>
                    <li>Assessor Data</li>
                    <li>Building Permits</li>
                    <li>Loan Model Data</li>
                    <li>Pre-Foreclosure Data</li>
                    <li>POI Data</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className={styles['th-faq-group']}>
              <div className={styles['th-faq-column']}>
                <div className={cn(styles['th-faq-column-header'], styles['th-quarterly'])}>Quarterly</div>
                <div>
                  <ul>
                    <li>Hazards Data</li>
                    <li>Neighborhood Boundaries</li>
                    <li>Metro Boundaries</li>
                    <li>Residential Boundaries</li>
                    <li>School Data</li>
                  </ul>
                </div>
              </div>
              <div className={styles['th-faq-column']}>
                <div className={cn(styles['th-faq-column-header'], styles['th-yearly'])}>Yearly</div>
                <div>
                  <ul>
                    <li>Community Data</li>
                    <li>County Boundaries</li>
                    <li>County Sub-Division Boundaries</li>
                    <li>Parcel Boundaries</li>
                    <li>Place Boundaries</li>
                    <li>State Boundaries</li>
                    <li>ZIP &#39;ZTCA&#39; Boundaries</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      question: 'Does TopHap cover commercial properties?',
      answer: (
        <div>Yes! You can show only commercial properties by using the
          property type filter and checking "Commercial".
        </div>
      )
    },
    {
      question: 'Does TopHap have an app?',
      answer: (
        <div>
          We have an iOS app available on the App Store:
          {' '}
          <a
            className='text-primary underline'
            href='https://apps.apple.com/us/app/tophap-explorer/id1551845829'
            rel='noreferrer'
            target='_blank'
          >
            TopHap Explorer
          </a>
          .
        </div>
      )
    }
  ]

  return (
    <Page
      className={cn('container', styles['th-faq-page'])}
      title='Frequently Asked Questions | TopHap'
    >
      <Head>
        <meta
          key='description'
          content='Get answers to the most frequently asked questions we receive from users.'
          name='description'
        />
      </Head>
      <h1 className={styles['th-page-title']}>Frequently Asked Questions</h1>

      <FAQ
        classes={{
          root: styles['th-faq-component'],
          question: styles['th-faq-question'],
          toggle: styles['th-faq-question-toggle'],
          answer: styles['th-faq-answer'],
          item: styles['th-faq-item'],
          itemExpanded: styles['th-expanded']
        }}
        items={items}
        noTitle
      />
    </Page>
  )
}

export default News
